import { useState } from "react";
import { Eye, Mail } from "lucide-react";
import { useUserProfile } from "@/components/auth";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useViewerCapabilities } from "@/lib/capabilities";
import { cn } from "@/lib/utils";
import { requestAccessHref, useAccessManagers } from "./request-access";

function editAccessHref(email: string, organizationName: string | null) {
  return requestAccessHref(
    email,
    "Edit access request",
    `Hi, could you make me a member${organizationName ? ` in ${organizationName}` : ""} so I can edit and process work?`
  );
}

/**
 * The one marker telling a reviewer why project pages have no edit controls, with a way to ask a
 * company admin for edit access. Renders nothing for anyone who can edit.
 */
export function ViewOnlyIndicator({ className }: { className?: string }) {
  const capabilities = useViewerCapabilities();
  const { userProfile } = useUserProfile();
  const [open, setOpen] = useState(false);
  const isViewOnly = capabilities !== null && !capabilities.canEditWork;
  // Only look up admins once the reviewer asks how to get access.
  const managers = useAccessManagers({ enabled: isViewOnly && open });
  if (!isViewOnly) return null;

  const organizationName = userProfile?.organizationName ?? null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-slot="view-only-indicator"
          className={cn(
            "inline-flex h-6 shrink-0 items-center gap-1 rounded-md border border-border bg-muted/50 px-2 text-xs font-semibold whitespace-nowrap text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className
          )}
        >
          <Eye className="size-3.5" aria-hidden="true" />
          View Only
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-3 border-2">
        <div className="space-y-1">
          <p className="text-sm font-bold text-foreground">View only — you're a reviewer</p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            You can view the projects you're added to and download completed exports. To create,
            upload, edit, or process work, ask a company admin to make you a member.
          </p>
        </div>
        {managers.length > 0 && (
          <ul className="space-y-2">
            {managers.map((manager) => (
              <li
                key={manager._id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border-2 border-border bg-card px-3 py-2"
              >
                <span className="min-w-0 truncate text-sm font-medium" title={manager.email}>
                  {manager.name}
                </span>
                <a
                  href={editAccessHref(manager.email, organizationName)}
                  className="inline-flex min-h-9 items-center gap-1.5 rounded-md px-2 text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Email ${manager.name} to request edit access`}
                >
                  <Mail className="size-4" aria-hidden="true" />
                  Request edit access
                </a>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
